import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreActions } from 'easy-peasy';
import { useAuthContext } from 'contexts/AuthContext';
import Modal from './Modal';
import { getSettablePumpStatusCodes } from './pumpStatusMeta';

export default function BulkUpdateStatusModal({ isOpen, pumpIds, onClose, onSuccess, anchorRect = null }) {
  const { t } = useTranslation();
  const { userRole } = useAuthContext();
  const bulkUpdatePumpStatus = useStoreActions((actions) => actions.pumps.bulkUpdatePumpStatus);
  const [status, setStatus] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const statusOptions = useMemo(() => {
    const codes = getSettablePumpStatusCodes(userRole);
    return codes.map((value) => ({ value, label: t(`pump_status_${value}`) }));
  }, [userRole, t]);

  useEffect(() => {
    if (!isOpen || statusOptions.length === 0) return;
    setStatus(statusOptions[0].value);
  }, [isOpen, statusOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await bulkUpdatePumpStatus({
        pump_ids: pumpIds,
        status,
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.status?.[0] || t('pump_bulk_update_error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('pump_bulk_modal_title')} size="medium" anchorRect={anchorRect}>
      <p className="text-muted mb-3">
        {t('pump_bulk_modal_body', { count: pumpIds.length })}
      </p>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">{t('pump_bulk_field_status')} *</label>
          <select
            className="form-select"
            value={status}
            onChange={(e) => setStatus(parseInt(e.target.value, 10))}
            required
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {error && (
          <div className="form-error mb-2">
            {error}
          </div>
        )}
        <div className="form-actions d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            {t('Peruuta')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t('pump_bulk_saving') : t('Save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
